/** Conversão entre linhas do banco (snake_case) e tipos do domínio. */
import type {
  CalendarEvent, CalendarId, Category, Frequency, Note, Priority, Project, Role, Routine, Subtask, Task,
  TaskHistoryEntry, TaskLink, TaskOrigin, TaskStatus, Theme, User,
} from '@quadro/domain';
import type {
  CategoryRow, EventRow, NoteRow, ProfileRow, ProjectRow, RoutineRow, SubcategoryRow, SubtaskRow, TaskHistoryRow, TaskRow, ThemeRow,
} from './rows';

export const toUser = (r: ProfileRow): User => ({
  id: r.id, name: r.name, email: r.email, color: r.color, role: r.role as Role, active: r.active,
});

export const toSubtask = (r: SubtaskRow): Subtask => ({ id: r.id, text: r.text, done: r.done, assigneeId: r.assignee_id, due: r.due });

export function toTask(r: TaskRow, subtasks: Subtask[]): Task {
  return {
    id: r.id, title: r.title, description: r.description, status: r.status as TaskStatus,
    projectId: r.project_id, assigneeId: r.assignee_id, priority: r.priority as Priority, due: r.due, position: r.position,
    waiting: r.status === 'waiting' ? { who: r.waiting_who || 'Terceiros', followUpOn: r.waiting_follow_up } : null,
    themeIds: r.theme_ids, entityIds: r.entity_ids,
    classification: r.category_id ? { categoryId: r.category_id, subcategoryId: r.subcategory_id } : null,
    subtasks, links: Array.isArray(r.links) ? (r.links as unknown as TaskLink[]) : [], routineId: r.routine_id, routineDate: r.routine_date, origin: (r.origin as TaskOrigin | null) ?? null,
    createdAt: r.created_at, createdById: r.created_by, enteredStatusAt: r.entered_status_at,
    startedAt: r.started_at, completedAt: r.completed_at, updatedAt: r.updated_at,
  };
}

/** Campos editáveis da tarefa → colunas. */
export function taskColumns(t: Partial<Task>): Partial<TaskRow> {
  const c: Partial<TaskRow> = {};
  if (t.title !== undefined) c.title = t.title;
  if (t.description !== undefined) c.description = t.description;
  if (t.status !== undefined) c.status = t.status;
  if (t.projectId !== undefined) c.project_id = t.projectId;
  if (t.assigneeId !== undefined) c.assignee_id = t.assigneeId;
  if (t.priority !== undefined) c.priority = t.priority;
  if (t.due !== undefined) c.due = t.due;
  if (t.position !== undefined) c.position = t.position;
  if (t.themeIds !== undefined) c.theme_ids = t.themeIds;
  if (t.entityIds !== undefined) c.entity_ids = t.entityIds;
  if (t.classification !== undefined) {
    c.category_id = t.classification?.categoryId ?? null;
    c.subcategory_id = t.classification?.subcategoryId ?? null;
  }
  if (t.waiting !== undefined) {
    c.waiting_who = t.waiting?.who ?? null;
    c.waiting_follow_up = t.waiting?.followUpOn ?? null;
  }
  if (t.links !== undefined) c.links = t.links as unknown as TaskRow['links'];
  if (t.origin !== undefined) c.origin = t.origin as unknown as TaskRow['origin'];
  return c;
}

export const toHistory = (r: TaskHistoryRow): TaskHistoryEntry => ({ at: r.at, text: r.text, byId: r.by_id });

export const toProject = (r: ProjectRow): Project => ({ ...r });
export const toTheme = (r: ThemeRow): Theme => ({ ...r });
export const toCategory = (r: CategoryRow, subs: SubcategoryRow[]): Category => ({
  id: r.id, name: r.name, position: r.position,
  subcategories: subs.filter(s => s.category_id === r.id).sort((a, b) => a.position - b.position).map(s => ({ id: s.id, name: s.name })),
});

export function toRoutine(r: RoutineRow): Routine {
  return {
    id: r.id, title: r.title, description: r.description, projectId: r.project_id, assigneeId: r.assignee_id,
    priority: r.priority as Priority, themeIds: r.theme_ids, entityIds: r.entity_ids,
    classification: r.category_id ? { categoryId: r.category_id, subcategoryId: r.subcategory_id } : null,
    startStatus: r.start_status as Routine['startStatus'], frequency: r.frequency as unknown as Frequency,
    startsOn: r.starts_on, leadDays: r.lead_days, checklist: r.checklist, active: r.active,
    generatedThrough: r.generated_through, createdById: r.created_by, createdAt: r.created_at,
  };
}
export function routineColumns(r: Omit<Routine, 'id' | 'generatedThrough' | 'createdById' | 'createdAt'>): Partial<RoutineRow> {
  return {
    title: r.title, description: r.description, project_id: r.projectId, assignee_id: r.assigneeId, priority: r.priority,
    theme_ids: r.themeIds, entity_ids: r.entityIds, category_id: r.classification?.categoryId ?? null,
    subcategory_id: r.classification?.subcategoryId ?? null, start_status: r.startStatus,
    frequency: r.frequency as unknown as RoutineRow['frequency'], starts_on: r.startsOn, lead_days: r.leadDays,
    checklist: r.checklist, active: r.active,
  };
}

const hhmm = (t: string) => t.slice(0, 5);
export const toEvent = (r: EventRow): CalendarEvent => ({
  id: r.id, title: r.title, location: r.location, date: r.date, start: hhmm(r.start_time), end: hhmm(r.end_time),
  calendar: r.calendar as CalendarId, createdById: r.created_by,
});
export const toNote = (r: NoteRow): Note => ({ id: r.id, title: r.title, body: r.body, pinned: r.pinned, updatedAt: r.updated_at });
